import { GoogleAnalyticsService } from "@/lib/googleAnalyticsService";
import { useStore } from "@/store";
import { useCallback } from "react";

export const useGoogleAnalyticsConnection = () => {
  const { currentUser } = useStore();

  // Connect new Google Analytics account
  const connectNewAccount = useCallback(
    async (redirectUrl?: string) => {
      if (!currentUser?.uid) {
        throw new Error("User not authenticated");
      }

      try {
        const response = await GoogleAnalyticsService.connectGA4(redirectUrl);

        // Redirect user to Google OAuth
        window.location.href = response.auth_url;
      } catch (err) {
        console.error("GA4 connection error:", err);
        throw err;
      }
    },
    [currentUser],
  );

  // Check connection status for specific account
  const checkAccountStatus = useCallback(
    async (accountId: string) => {
      if (!currentUser?.uid) {
        throw new Error("User not authenticated");
      }

      try {
        const status = await GoogleAnalyticsService.checkGA4Status(accountId);
        return status;
      } catch (err) {
        console.error("GA4 status check error:", err);
        throw err;
      }
    },
    [currentUser],
  );

  // Disconnect specific account
  const disconnectAccount = useCallback(
    async (accountId: string) => {
      if (!currentUser?.uid) {
        throw new Error("User not authenticated");
      }

      try {
        await GoogleAnalyticsService.disconnectGA4(accountId);
      } catch (err) {
        console.error("GA4 disconnect error:", err);
        throw err;
      }
    },
    [currentUser],
  );

  // Disconnect account and purge all domain data
  const disconnectAndPurge = useCallback(
    async (accountId: string, domainId: string) => {
      if (!currentUser?.uid) {
        throw new Error("User not authenticated");
      }

      try {
        const response = await GoogleAnalyticsService.disconnectAndPurgeGA4(
          accountId,
          domainId,
        );
        return response;
      } catch (err) {
        console.error("GA4 disconnect and purge error:", err);
        throw err;
      }
    },
    [currentUser],
  );

  // Handle OAuth callback
  const handleOAuthCallback = useCallback(() => {
    const callbackParams = GoogleAnalyticsService.parseCallbackParams(
      window.location.href,
    );
    return callbackParams;
  }, []);

  return {
    // Connection management only
    connectNewAccount,
    checkAccountStatus,
    disconnectAccount,
    disconnectAndPurge,
    handleOAuthCallback,
  };
};
